/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ApiError, NodeResourceUtilizationResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { NODE_TELEMETRY_QUERY_KEY_ROOT } from './useNodeTelemetryHistogramQuery';

export type UseNodeResourceUtilizationQueryParams = {
  nodeId: string;
  since: string;
  until: string;
  // false disables background refetching; the caller pauses ticking too so
  // the window stays frozen.
  refetchIntervalMs?: number | false;
};

export function useNodeResourceUtilizationQuery({
  nodeId,
  since,
  until,
  refetchIntervalMs = 60_000,
}: UseNodeResourceUtilizationQueryParams) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  return useQuery<NodeResourceUtilizationResponse, ApiError>({
    queryKey: [NODE_TELEMETRY_QUERY_KEY_ROOT, 'resource-utilization', tenantId, nodeId, since, until],
    enabled: !!tenantId && !!nodeId,
    placeholderData: keepPreviousData,
    refetchInterval: refetchIntervalMs,
    queryFn: () =>
      mgmtPlaneAPIClient.nodes.getNodeResourceUtilization({
        tenantId: tenantId!,
        nodeId,
        since,
        until,
      }),
  });
}
